import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { TaskResponse } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import { toast } from 'src/components/ui/toast';
import { useCan } from 'src/hooks/useCan';
import type { OnChangeFn, RowSelectionState } from '@/lib/tanstack-table';
import {
  emptyScopedSelection,
  otherAgentReturnableTasks,
  partitionReassignableTasks,
  partitionReturnableTasks,
  rowsInScope,
  selectedIdsOf,
  updateScopedSelection,
  visibleSelectedTaskIds,
} from './taskQueueBulkActions';

/**
 * Row selection + bulk actions for the tasks queue. Selection is keyed by
 * task id and only ever acts on rows of the current page (`tasks`): it is
 * pinned to `scopeKey` (the serialized list query — page, filters, sort,
 * search) and dropped as soon as that scope changes, so a selection made on
 * another page or under another filter can never be silently carried along.
 *
 * "Return to Agent" re-queues failed / needs_input tasks with the agent that
 * last worked them (`tasks.batchReturnToAgent`); its "different agent"
 * variant re-queues them with one agent the user picks instead. "Reassign"
 * hands the selected open tasks to one person or agent (`tasks.batchAssign`).
 */
export function useTaskQueueBulkActions(
  tasks: readonly TaskResponse[],
  scopeKey: string,
) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [canUpdateTasks] = useCan(Action.Update, Subject.Task);
  const [selection, setSelection] = useState(() =>
    emptyScopedSelection(scopeKey),
  );
  if (selection.scopeKey !== scopeKey) {
    // Scope changed since the last render: forget the stale selection for
    // good rather than only hiding it, so toggling a filter back to the same
    // scope does not resurrect rows the user saw deselected. (React's
    // "adjust state when a prop changes" pattern — no effect, no extra commit.)
    setSelection(emptyScopedSelection(scopeKey));
  }

  const rowSelection = rowsInScope(selection, scopeKey);
  const setRowSelection: OnChangeFn<RowSelectionState> = (update) => {
    setSelection((previous) =>
      updateScopedSelection(previous, scopeKey, update),
    );
  };

  const selectedIds = selectedIdsOf(rowSelection);
  const { returnable, skipped } = partitionReturnableTasks(tasks, selectedIds);
  const { reassignable } = partitionReassignableTasks(tasks, selectedIds);
  const otherAgentReturnable = otherAgentReturnableTasks(tasks, selectedIds);
  const selectedCount = returnable.length + skipped.length;

  function clearSelection() {
    setSelection(emptyScopedSelection(scopeKey));
  }

  const returnToAgent = useMutation({
    mutationFn: (input: ReturnToAgentInput) =>
      rpcClient.tasks.batchReturnToAgent({
        taskIds: input.taskIds,
        agentDefinitionId: input.agent?.id,
      }),
    onSuccess: (returned, input) => {
      invalidateTaskQueries(queryClient);
      clearSelection();
      toast.success(
        input.agent
          ? sentToAgentMessage(gt, returned.length, input.agent.name)
          : returnedToAgentMessage(gt, returned.length),
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not return tasks to their agents', {
              context: 'toast after a bulk return-to-agent failed',
            }),
      );
    },
  });

  function returnSelectedToAgent() {
    if (returnable.length === 0) return;
    returnToAgent.mutate({ taskIds: returnable.map((task) => task.id) });
  }

  /** Re-queue the failed / needs-input selection with `agent` instead. */
  function returnSelectedToOtherAgent(agent: { id: string; name: string }) {
    if (otherAgentReturnable.length === 0) return;
    returnToAgent.mutate({
      taskIds: otherAgentReturnable.map((task) => task.id),
      agent,
    });
  }

  return {
    /** Whether the selection column and bulk bar render at all. */
    enabled: canUpdateTasks,
    rowSelection,
    setRowSelection,
    selectedCount,
    selectedIds: visibleSelectedTaskIds(tasks, selectedIdsOf(rowSelection)),
    returnableCount: returnable.length,
    otherAgentReturnableCount: otherAgentReturnable.length,
    reassignableCount: reassignable.length,
    reassignableIds: reassignable.map((task) => task.id),
    clearSelection,
    returnSelectedToAgent,
    returnSelectedToOtherAgent,
    isReturning: returnToAgent.isPending,
  };
}

export type TaskQueueBulkActions = ReturnType<typeof useTaskQueueBulkActions>;

type ReturnToAgentInput = {
  taskIds: string[];
  /** Omit to return each task to the agent that last worked it. */
  agent?: { id: string; name: string };
};

/** gt-react has no plural forms, so pick the source string explicitly. */
function returnedToAgentMessage(
  gt: ReturnType<typeof useGT>,
  count: number,
): string {
  const context =
    'toast after bulk-returning failed or needs-input tasks to the agent that worked them';
  return count === 1
    ? gt('Returned {count} task to its agent', { count, context })
    : gt('Returned {count} tasks to their agents', { count, context });
}

function sentToAgentMessage(
  gt: ReturnType<typeof useGT>,
  count: number,
  agentName: string,
): string {
  const context =
    'toast after sending failed or needs-input tasks to an agent the user picked';
  return count === 1
    ? gt('Sent {count} task to {agentName}', { count, agentName, context })
    : gt('Sent {count} tasks to {agentName}', { count, agentName, context });
}
